import { Service } from '@/api/services';
import ServiceCard from '../ServiceCard/ServiceCard';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDragScroll } from '@/hooks/use-drag-scroll';
import Button from '../Button';
import SliderArrowSvg from '@/svg/arrow.svg';
import { useMediaQuery } from 'usehooks-ts';

interface Props {
    services: Service[];
}

const widths = {
    isDesktop: 330,
    isLaptop: 276,
    isTablet: 295,
};

const ServiceDetailCardsList = ({ services }: Props) => {
    const isDesktop = useMediaQuery('(min-width: 1800px) and (max-width: 1920px)');
    const isLaptop = useMediaQuery('(min-width: 1200px) and (max-width: 1799px)');
    const isTablet = useMediaQuery('(max-width: 1199px)');

    const currentWidth = isDesktop
        ? widths['isDesktop']
        : isLaptop
          ? widths['isLaptop']
          : isTablet
            ? widths['isTablet']
            : 100;

    const servicesListRef = useRef<HTMLUListElement>(null);
    useDragScroll(servicesListRef);

    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(true);

    const checkScroll = useCallback(() => {
        const list = servicesListRef.current;
        if (!list) return;

        const { scrollLeft, scrollWidth, clientWidth } = list;
        setShowPrev(scrollLeft > 0);
        setShowNext(scrollLeft + clientWidth < scrollWidth);
    }, []);

    useEffect(() => {
        const list = servicesListRef.current;
        if (!list) return;

        const resizeObserver = new ResizeObserver(checkScroll);
        resizeObserver.observe(list);

        list.addEventListener('scroll', checkScroll);
        return () => {
            list.removeEventListener('scroll', checkScroll);
            resizeObserver.unobserve(list);
        };
    }, [checkScroll]);

    const handleNext = () => {
        const list = servicesListRef.current;
        if (!list) return;

        list.scrollBy({
            left: currentWidth,
            behavior: 'smooth',
        });
    };

    const handlePrev = () => {
        const list = servicesListRef.current;
        if (!list) return;

        list.scrollBy({
            left: -currentWidth,
            behavior: 'smooth',
        });
    };

    return (
        services.length > 0 && (
            <div className="product-services">
                <div className="product-services__top">
                    <div className="h2 product-services__title">Услуги</div>
                    <div className="product-services__controls">
                        <Button
                            className="product-services__control product-services__control--prev"
                            onClick={handlePrev}
                            disabled={!showPrev}
                            geometryVariant="mustache"
                            size="sm"
                            aria-label="Предыдущий слайд"
                        >
                            <SliderArrowSvg />
                        </Button>
                        <Button
                            className="product-services__control product-services__control--next"
                            onClick={handleNext}
                            disabled={!showNext}
                            geometryVariant="mustache"
                            size="sm"
                            aria-label="Следующий слайд"
                        >
                            <SliderArrowSvg />
                        </Button>
                    </div>
                </div>
                <ul ref={servicesListRef} className="service-cards-list list-unstyled">
                    {services.map((service) => (
                        <li className="service-cards-list__item" key={service.slug}>
                            <ServiceCard
                                title={service.name}
                                description={service.description}
                                feedbackPopupDataName={service.slug}
                                isActive
                            />
                        </li>
                    ))}
                </ul>
            </div>
        )
    );
};

export default ServiceDetailCardsList;
